import { useEffect, useRef, useState } from "react";
import { Form, Spin } from "antd";
import { StyledGridRow, StyledSectionHeading } from "@/common/styles";
import useActiveWarrant from "@/hooks/useActiveWarrant";
import DynamicForm from "@/common/DynamicForm";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import { useSelector } from "react-redux";
import { convertApiFields, isNonEmptyVariable } from "@/util/common_function";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import dayjs from "dayjs";

const toLabel = (columnName) => {
  const withoutSuffix = columnName.replace(/_U$/i, "");
  return withoutSuffix
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Module-level cache for field definitions — fetched once per SPA session
let _cachedFieldDefs = null;

const AdditionalWarrantInformation = () => {
  const [form] = Form.useForm();
  const { activeWarrant, bookingsFieldsEnabled, patch } = useActiveWarrant();
  const fullSearch = useSelector((state) => state.fullSearch);

  const [apiFields,       setApiFields]       = useState([]);
  const [extraFieldKeys,  setExtraFieldKeys]  = useState([]);
  const [fieldTypeMap,    setFieldTypeMap]     = useState({});
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [isLoadingData,   setIsLoadingData]   = useState(false);

  const prevDataWarrantIdRef = useRef(null);
  const fieldsFetchedRef     = useRef(false);
  const fieldTypeMapRef      = useRef({});
  const extraFieldKeysRef    = useRef([]);

  // Debounce ref for onValuesChange — prevents Redux dispatch on every keystroke
  const debounceValuesChange = useRef(null);

  // Stable ref to activeWarrant so the debounced callback always reads latest value
  const activeWarrantRef = useRef(activeWarrant);
  useEffect(() => { activeWarrantRef.current = activeWarrant; }, [activeWarrant]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => { if (debounceValuesChange.current) clearTimeout(debounceValuesChange.current); };
  }, []);

  // ── 1. Fetch field definitions once on mount, then immediately fetch data ──
  useEffect(() => {
    if (fieldsFetchedRef.current) return;
    fieldsFetchedRef.current = true;

    const warrantId = activeWarrant?.warrantId ?? activeWarrant?.warrantID ?? null;

    fetchWarrantExtraFields().then(({ typeMap, converted }) => {
      const existingExtra = activeWarrant?.warrantsExtra?.[0];
      
      if (existingExtra) {
        const formValues = Object.keys(existingExtra).reduce((acc, key) => {
          const val = existingExtra[key];
          const dataType = typeMap[key] ?? "";
          acc[key] = dataType === "datetime" && val != null ? dayjs(val) : val;
          return acc;
        }, {});
        form.setFieldsValue(formValues);
        setApiFields(converted);
        prevDataWarrantIdRef.current = warrantId;        
        return;
      }

      // No local data. No warrant ID either — brand new warrant, show empty fields.
      if (!warrantId) {
        setApiFields(converted);
        form.resetFields();
        return;
      }
      
      prevDataWarrantIdRef.current = warrantId;
      fetchExtraFieldsData(warrantId, typeMap, converted);
    });
  }, []);

  // ── 2. Re-fetch data when the active warrant changes after initial mount ──
  useEffect(() => {
    if (!fieldsFetchedRef.current) return;
    if (!activeWarrant) return;

    const warrantId = activeWarrant?.warrantId ?? activeWarrant?.warrantID ?? null;

    if (warrantId === prevDataWarrantIdRef.current) return;
    prevDataWarrantIdRef.current = warrantId;

    if (!warrantId) {
      // Switched to a new (unsaved) warrant — reset form, clear extra
      form.resetFields();
      patch({ warrantsExtra: [] });
      return;
    }

    // Fields already rendered from mount; pass null so we don't re-set them
    const existingExtra = activeWarrant?.warrantsExtra?.[0];
    if (!existingExtra) {
      fetchExtraFieldsData(warrantId, fieldTypeMapRef.current, null);
    }
  }, [activeWarrant?.warrantId, activeWarrant?.warrantID]);

  // ── Fetch field definitions from API ─────────────────────────────────────
  const fetchWarrantExtraFields = () => {
    if (_cachedFieldDefs) {
      setFieldTypeMap(_cachedFieldDefs.typeMap);
      setExtraFieldKeys(_cachedFieldDefs.extraFieldKeys);
      fieldTypeMapRef.current     = _cachedFieldDefs.typeMap;
      extraFieldKeysRef.current   = _cachedFieldDefs.extraFieldKeys;
      return Promise.resolve(_cachedFieldDefs);
    }

    setIsLoadingFields(true);

    return api
      .get(API_ENDPOINTS.warrants.getWarrantExtra)
      .then((response) => {
        const tableData = (response?.data ?? []).filter(
          (d) => d?.columnName !== "WarrantExtraID" && d?.columnName !== "WarrantID"
        );

        const keys = tableData.map((d) => d.columnName);
        setExtraFieldKeys(keys);
        extraFieldKeysRef.current = keys;

        const typeMap = tableData.reduce((acc, d) => {
          acc[d.columnName] = d.dataType?.toLowerCase() ?? "";
          return acc;
        }, {});
        setFieldTypeMap(typeMap);
        fieldTypeMapRef.current = typeMap;

        const converted = convertApiFields(
          tableData.map((d) => ({
            ...d,
            columnName: d.columnName,
            label:      toLabel(d.columnName),
          })),
          "WarrantsExtra", true
        );

        _cachedFieldDefs = { typeMap, converted, extraFieldKeys: keys };
        return { typeMap, converted };
      })
      .catch(() => ({ typeMap: {}, converted: [] }))
      .finally(() => setIsLoadingFields(false));
  };

  // ── Fetch existing extra-field data for a saved warrant ──────────────────
  const fetchExtraFieldsData = (warrantId, typeMap = {}, converted = null) => {
    const url = `${API_ENDPOINTS.warrants.getExtraFieldsData}WarrantsExtra/warrantID/${warrantId}`;
    setIsLoadingData(true);

    api
      .get(url)
      .then((response) => {
        const data = response?.data;

        if (isNonEmptyVariable(data)) {
          const extraRow = Array.isArray(data) ? data[data.length - 1] : data;

          if (extraRow) {
            const formValues = Object.keys(extraRow).reduce((acc, key) => {
              const val      = extraRow[key];
              const dataType = typeMap[key] ?? "";
              if (dataType === "datetime" && val !== null && val !== undefined) {
                const dayjsVal = dayjs(val);
                acc[key] = dayjsVal.isValid() ? dayjsVal : val;
              } else {
                acc[key] = val;
              }
              return acc;
            }, {});

            form.setFieldsValue(formValues);

            patch({
              warrantsExtra: [{
                ...extraRow,
                warrantExtraID: extraRow?.WarrantExtraID ?? extraRow?.warrantExtraID ?? 0,
                warrantID:      warrantId,
              }],
            });

            if (converted) setApiFields(converted);
          } else {
            form.resetFields();
            if (converted) setApiFields(converted);
          }
        } else {
          form.resetFields();
          if (converted) setApiFields(converted);
        }
      })
      .catch(() => {
        form.resetFields();
        if (converted) setApiFields(converted);
      })
      .finally(() => setIsLoadingData(false));
  };

  // ── Debounced onValuesChange — replaces both handleValuesChange and handleBlur
  // Text fields: Redux update fires 300ms after the user stops typing.
  // Date/select fields: effectively immediate since they fire once on change.
  const handleValuesChange = (_, allValues) => {
    if (debounceValuesChange.current) clearTimeout(debounceValuesChange.current);
    debounceValuesChange.current = setTimeout(() => {
      const aw = activeWarrantRef.current;
      const existingExtra = aw?.warrantsExtra?.[0] ?? {};
      const keys = extraFieldKeysRef.current;

      const extraValues = keys.reduce((acc, key) => {
        if (key in allValues) {
          const val = allValues[key];
          acc[key] = val && typeof val === "object" && typeof val?.format === "function"
            ? val.format("YYYY-MM-DD HH:mm:ss")
            : val;
        }
        return acc;
      }, {});

      patch({
        warrantsExtra: [{
          ...existingExtra,
          ...extraValues,
          warrantExtraID: existingExtra?.warrantExtraID ?? 0,
          warrantID:
            existingExtra?.warrantID ??
            aw?.warrantId ??
            aw?.warrantID ??
            0,
        }],
      });
    }, 300);
  };

  const isLoading = isLoadingFields || isLoadingData;

  // Nothing configured (or still figuring that out) for this customer's
  // WarrantsExtra columns — hide the whole section (header included) rather
  // than showing an empty "Additional Warrant Information" block, same as
  // AdditionalStaffInformation.jsx's `if (!apiFields.length) return null`.
  if (!isLoading && apiFields.length === 0) return null;

  return (
    <>
      <StyledSectionHeading>
        <span>Additional Warrant Information</span>
      </StyledSectionHeading>

      <FieldSecurityProvider
        entity="WarrantsExtra"
        startingPoint="Warrants"
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <Spin spinning={isLoading}>
          <Form
            form={form}
            disabled={!bookingsFieldsEnabled}
            onValuesChange={handleValuesChange}
          >
            <StyledGridRow gutter={16}>
              {apiFields.map((sf, index) => (
                <DynamicForm
                  key={index}
                  formAttributes={sf}
                  form={form}
                  bookingsFieldsEnabled={bookingsFieldsEnabled}
                  fullSearch={fullSearch}
                />
              ))}
            </StyledGridRow>
          </Form>
        </Spin>
      </FieldSecurityProvider>
    </>
  );
};

export default AdditionalWarrantInformation;
